import React, { useState, useEffect, useRef } from 'react';
import { 
  MapPin, 
  X, 
  RefreshCw, 
  Check,
  RotateCcw,
  ExternalLink
} from 'lucide-react';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import { mapCacheService, type AddressData } from '@/services/maps/MapCacheService';
import { geolocationService } from '@/services/geolocation/GeolocationService';

// Nota: Este componente requer a biblioteca Leaflet
// É necessário instalar as dependências:
// npm install leaflet
// npm install @types/leaflet

interface MapLocationSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onLocationSelected: (lat: number, lng: number, address: AddressData | null) => void;
}

const MapLocationSelector: React.FC<MapLocationSelectorProps> = ({ 
  isOpen, 
  onClose,
  onLocationSelected
}) => {
  const [selectedLocation, setSelectedLocation] = useState<{lat: number, lng: number} | null>(null);
  const [address, setAddress] = useState<AddressData | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);
  
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const circleRef = useRef<any>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  // Inicializar o mapa quando o modal for aberto
  useEffect(() => {
    if (isOpen && !mapInitialized && mapContainerRef.current) {
      // Importar Leaflet dinamicamente para evitar problemas de SSR
      import('leaflet').then((L) => {
        // Verificar se o mapa já foi inicializado
        if (mapRef.current) return;
        
        // Fix Leaflet icon issue
        // This is needed because Leaflet's default icon paths are based on CSS urls
        const DefaultIcon = L.icon({
          iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
          iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
          shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        });
        
        L.Marker.prototype.options.icon = DefaultIcon;
        
        // Inicializar o mapa
        if (mapContainerRef.current) {
          mapRef.current = L.map(mapContainerRef.current).setView([0, 0], 13);
          
          // Adicionar camada de tiles do OpenStreetMap
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors'
          }).addTo(mapRef.current);
        }
        
        // Adicionar evento de clique no mapa
        mapRef.current.on('click', (e: any) => {
          const { lat, lng } = e.latlng;
          handleLocationSelect(lat, lng);
        });
        
        // Obter localização atual
        getCurrentLocation();
        
        setMapInitialized(true);
      });
    }
    
    // Limpar o mapa quando o modal for fechado
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
        circleRef.current = null;
        setMapInitialized(false);
      }
    };
  }, [isOpen]);

  // Obter localização atual
  const getCurrentLocation = async () => {
    setIsLoadingLocation(true);
    
    try {
      // Verificar se a geolocalização está disponível
      if (!navigator.geolocation) {
        alert('Geolocalização não é suportada neste navegador.');
        return;
      }

      // Verificar permissão de geolocalização
      const permissionStatus = await geolocationService.checkPermission();
      if (permissionStatus === 'denied') {
        alert('Permissão de localização negada. Verifique as configurações do seu navegador.');
        return;
      }
      
      // Usar o serviço de geolocalização com timeout maior
      const position = await geolocationService.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 30000, // 30 segundos
        maximumAge: 0 // Forçar uma nova leitura
      });
      
      // Atualizar o mapa com a localização atual
      if (mapRef.current) {
        mapRef.current.setView([position.lat, position.lng], 15);
        handleLocationSelect(position.lat, position.lng);
      }
    } catch (error: any) {
      console.error('Erro ao obter localização:', error);
      
      // Mensagens de erro mais amigáveis
      let errorMsg = error.message || 'Erro desconhecido';
      if (errorMsg === 'Location timeout') {
        errorMsg = 'Tempo esgotado ao tentar obter sua localização. Verifique se seu GPS está ativado.';
      } else if (errorMsg === 'Location unavailable') {
        errorMsg = 'Não foi possível determinar sua localização. Verifique se o GPS está ativado.';
      }
      
      alert(errorMsg);
    } finally {
      setIsLoadingLocation(false);
    }
  };

  // Selecionar localização no mapa
  const handleLocationSelect = async (lat: number, lng: number) => {
    // Atualizar estado
    setSelectedLocation({ lat, lng });
    
    // Adicionar ou mover marcador
    if (mapRef.current) {
      const L = await import('leaflet');
      
      if (markerRef.current) {
        markerRef.current.setLatLng([lat, lng]);
      } else {
        markerRef.current = L.marker([lat, lng]).addTo(mapRef.current);
      }

      // Adicionar ou atualizar círculo de raio baseado nas configurações (km -> metros)
      const radiusKm = mapCacheService.getSettings().cacheRadius;
      const radiusMeters = Math.max(0, radiusKm) * 1000;
      if (radiusMeters > 0) {
        if (circleRef.current) {
          circleRef.current.setLatLng([lat, lng]);
          circleRef.current.setRadius(radiusMeters);
        } else {
          circleRef.current = L.circle([lat, lng], {
            radius: radiusMeters,
            color: '#3b82f6', // blue-500
            weight: 1,
            fillColor: '#3b82f6',
            fillOpacity: 0.12,
          }).addTo(mapRef.current);
        }
      }
    }
    
    // Buscar endereço para a localização selecionada
    fetchAddress(lat, lng);
  };

  // Buscar endereço para a localização selecionada
  const fetchAddress = async (lat: number, lng: number) => {
    setIsLoadingAddress(true);
    setAddress(null);
    
    try {
      // Primeiro, verificar se temos o endereço em cache
      const cachedAddress = mapCacheService.findCachedAddress(lat, lng);
      
      if (cachedAddress) {
        setAddress(cachedAddress);
        return;
      }
      
      // Se não estiver em cache e estivermos online, buscar da API
      if (navigator.onLine) {
        const addressData = await mapCacheService.preloadAddressForLocation(lat, lng);
        setAddress(addressData);
      } else {
        alert('Você está offline. Não é possível buscar o endereço.');
      }
    } catch (error) {
      console.error('Erro ao buscar endereço:', error);
    } finally {
      setIsLoadingAddress(false);
    }
  };

  // Confirmar a seleção
  const handleConfirm = () => {
    if (selectedLocation) {
      onLocationSelected(selectedLocation.lat, selectedLocation.lng, address);
    }
  };

  // Reset da seleção
  const handleReset = () => {
    setSelectedLocation(null);
    setAddress(null);
    
    if (markerRef.current && mapRef.current) {
      mapRef.current.removeLayer(markerRef.current);
      markerRef.current = null;
    }

    if (circleRef.current && mapRef.current) {
      mapRef.current.removeLayer(circleRef.current);
      circleRef.current = null;
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Selecionar Localização no Mapa">
      <div className="space-y-6">
        {/* Instruções */}
        <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-md">
          <p>Escolha um local no mapa. Clique para confirmar. E então salvará na memória para você usar para cadastro de novos contatos</p>
        </div>

        {/* Mapa */}
        <div className="relative">
          <div 
            ref={mapContainerRef} 
            className="w-full h-64 rounded-md border border-gray-300"
          />
          
          {/* Botão de localização atual */}
          <div className="absolute top-2 right-2 z-[1000]">
            <button
              onClick={getCurrentLocation}
              disabled={isLoadingLocation}
              className="bg-white p-2 rounded-md shadow-md hover:bg-gray-100"
              title="Usar localização atual"
            >
              {isLoadingLocation ? (
                <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />
              ) : (
                <MapPin className="w-5 h-5 text-blue-500" />
              )}
            </button>
          </div>
        </div>

        {/* Coordenadas */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">Coordenadas</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Latitude
              </label>
              <input
                type="text"
                value={selectedLocation ? selectedLocation.lat.toFixed(6) : ''}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Longitude
              </label>
              <input
                type="text"
                value={selectedLocation ? selectedLocation.lng.toFixed(6) : ''}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
              />
            </div>
          </div>
          
          {selectedLocation && (
            <div className="mt-2">
              <a
                href={`https://www.google.com/maps?q=${selectedLocation.lat},${selectedLocation.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                Ver no Google Maps
              </a>
            </div>
          )}
        </div>

        {/* Endereço */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">Endereço</h3>
          
          {isLoadingAddress ? (
            <div className="text-center py-4">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-blue-500" />
              <p className="mt-2 text-sm text-gray-600">Buscando endereço...</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rua
                </label>
                <input
                  type="text"
                  value={address ? `${address.road || ''} ${address.house_number || ''}`.trim() : ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bairro
                </label>
                <input
                  type="text"
                  value={address?.suburb || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cidade
                  </label>
                  <input
                    type="text"
                    value={address?.city || ''}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    UF
                  </label>
                  <input
                    type="text"
                    value={address?.state || ''}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Botões de ação */}
        <div className="flex justify-between">
          <div>
            <Button variant="secondary" onClick={onClose}>
              <X className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
          </div>
          
          <div className="space-x-3">
            <Button 
              variant="secondary" 
              onClick={handleReset}
              disabled={!selectedLocation}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset
            </Button>
            
            <Button 
              onClick={handleConfirm}
              disabled={!selectedLocation}
            >
              <Check className="w-4 h-4 mr-2" />
              Confirmar
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default MapLocationSelector;
